import { useEffect, useState } from "react";
import api from "@/lib/api";
import { saleAct, undoSale, useSales } from "@/lib/sales";
import { UserName } from "@/components/UserName";
import { StateChip, Missing, ForSaleAs } from "@/components/sales/SalesRow";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BadgeDollarSign, Loader2 } from "lucide-react";
import { SalesActions } from "@/components/sales/SalesActions";

// THE SALES MENU on a part or a truck page (S2.1) — where "Flag to sell" sat. It shows where the item stands for
// sale and offers only what this person may do. Nothing here changes the item's own record; every action has Undo.
export function SalesMenuButton({ kind, id, label, builder, onChanged }) {
  const sales = useSales();
  const [open, setOpen] = useState(false);
  if (!sales.any && !(kind === "truck" && builder && sales.recon)) return null;
  return (
    <>
      <Button data-testid={`${kind}-sales-menu-open`} variant="outline" onClick={() => setOpen(true)} className="rounded-lg border-slate-300 h-9">
        <BadgeDollarSign className="w-4 h-4 mr-1.5" />Sales
      </Button>
      {open && <SalesMenuDialog kind={kind} id={id} label={label} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </>
  );
}

function SalesMenuDialog({ kind, id, label, onClose, onChanged }) {
  const [s, setS] = useState(null);
  const [last, setLast] = useState(null);
  const load = () => api.get(`/sales/item/${kind}/${id}`).then((r) => setS(r.data)).catch(() => setS({ error: true }));
  useEffect(() => { load(); }, [kind, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const after = () => { load(); onChanged?.(); };
  // the dialog sits above the toast, so the change just made carries its own Undo here too
  const act = async (m, p, b) => { const d = await saleAct(m, `/item/${kind}/${id}${p}`, b, after, true); if (d?.event_id) setLast(d); };
  const undo = async () => { if (await undoSale(last.event_id, after)) setLast(null); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sales-menu" className="rounded-xl max-w-lg w-[calc(100%-1.5rem)] sm:w-full">
        <DialogHeader><DialogTitle className="flex items-center gap-2 pr-8"><BadgeDollarSign className="w-5 h-5 text-[#B01E28]" />Sales · {label}</DialogTitle></DialogHeader>
        {!s ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /> : s.error
          ? <p className="text-sm text-slate-600">Couldn't read where this stands for sale. Close this and open it again.</p>
          : <SalesStanding s={s} kind={kind} label={label} act={act} />}
        {last && (
          <div data-testid="sales-menu-last" className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900">
            <span className="flex-1 min-w-0">{last.message}</span>
            <button type="button" data-testid="sales-menu-undo" onClick={undo}
              className="h-11 px-4 rounded-lg border-2 border-emerald-700 bg-white text-emerald-800 font-bold hover:bg-emerald-100">Undo</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SalesStanding({ s, kind, label, act }) {
  return (
    <div className="space-y-3">
      <div data-testid="sales-menu-standing" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
        {s.in_queue
          ? <p className="flex flex-wrap items-center gap-2">In the sales queue <StateChip state={s.state} words={s.state_words} testid="sales-menu-state" /></p>
          : <p data-testid="sales-menu-not-in-queue">Not in the sales queue.</p>}
        {s.for_sale_as && <div><ForSaleAs a={s.for_sale_as} testid="sales-menu-as" /></div>}
        {s.entered && <p data-testid="sales-menu-entered" className="text-xs text-slate-600">{s.entered.reason}{s.entered.note ? ` — ${s.entered.note}` : ""} · <UserName email={s.entered.by} /> · {(s.entered.at || "").slice(0, 10)}</p>}
        {Object.entries(s.marks).filter(([k]) => !(s.entered && k === s.entered.how)).map(([k, m]) => <p key={k} data-testid={`sales-menu-mark-${k}`} className="text-xs font-semibold text-slate-800">{m.words} · <UserName email={m.by} /> · {(m.at || "").slice(0, 10)}{m.note ? ` — ${m.note}` : ""}</p>)}
        {s.old_flag && !s.in_queue && <p data-testid="sales-menu-old-flag" className="text-xs text-slate-600">Flagged as a sell candidate by <UserName email={s.old_flag.by} /> — it shows on Recommended to sell.</p>}
        <Missing missing={s.complete ? [] : s.missing} testid="sales-menu-missing" lead={s.in_queue ? "To fix" : "Not complete yet — it can't go up for sale until it has"} />
      </div>
      <SalesActions s={s} kind={kind} label={label} act={act} />
    </div>
  );
}
